import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { resolveUrl as resolveCoverUrl } from "@/components/CoverUpload";
import { ArrowUpRight, BookOpen, Calendar, Tag, ShoppingCart, BookCheck, Home } from "lucide-react";

const STATUS_LABEL = {
  draft: { label: "Working", tone: "var(--bf-text-muted)", bg: "var(--bf-surface)" },
  prelaunch: { label: "Pre-launch", tone: "white", bg: "var(--bf-accent)" },
  published: { label: "Published", tone: "white", bg: "var(--bf-primary)" },
};

export default function PublicAuthor() {
  const { authorSlug } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/${authorSlug}`);
        setData(data);
        setStatus("ok");
      } catch {
        setStatus("notfound");
      }
    })();
  }, [authorSlug]);

  if (status === "loading") return <div className="min-h-screen flex items-center justify-center" data-testid="public-author-loading">Loading…</div>;
  if (status === "notfound") {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="public-author-notfound">
        <div className="text-center">
          <h1 className="font-serif text-5xl mb-3">Author not found</h1>
          <Link to="/" className="underline">Back home</Link>
        </div>
      </div>
    );
  }

  const { author, books } = data;
  const counts = books.reduce(
    (acc, b) => {
      acc.all += 1;
      acc[b.status] = (acc[b.status] || 0) + 1;
      return acc;
    },
    { all: 0, draft: 0, prelaunch: 0, published: 0 }
  );
  const visible = filter === "all" ? books : books.filter((b) => b.status === filter);
  const filterTabs = [
    { key: "all", label: "All books" },
    { key: "published", label: "Published" },
    { key: "prelaunch", label: "Pre-launch" },
    { key: "draft", label: "Working" },
  ];

  return (
    <div className="min-h-screen" style={{ background: "var(--bf-bg)" }} data-testid="public-author-page">
      <div
        className="relative"
        style={{
          backgroundImage:
            "url(https://static.prod-images.emergentagent.com/jobs/e21241af-dc17-4ac1-b5d0-f39caea73c8a/images/55410c45e12b57513424c2e1a6867ae80550bc7bb92f2b3775f48e468dbc0dca.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="backdrop-blur-sm bg-white/60">
          <header className="max-w-5xl mx-auto px-6 lg:px-12 pt-10 pb-6 flex justify-between items-center">
            <Link to="/" className="font-serif text-2xl" data-testid="public-brand">Bookforge</Link>
            <div className="flex items-center gap-4">
              <div className="text-xs label-eyebrow hidden sm:block" data-testid="public-author-handle">/{author.slug}</div>
              <Link to="/" data-testid="public-author-home-link">
                <Button variant="outline" size="sm" className="h-9">
                  <Home className="w-4 h-4 mr-1.5" /> Home
                </Button>
              </Link>
            </div>
          </header>
          <section className="max-w-5xl mx-auto px-6 lg:px-12 py-24">
            <div className="label-eyebrow mb-5">Author</div>
            <h1 className="font-serif text-6xl sm:text-7xl mb-6" data-testid="public-author-name">{author.name}</h1>
            <p className="text-base" style={{ color: "var(--bf-text-muted)" }} data-testid="public-author-stats">
              <strong>{counts.all}</strong> {counts.all === 1 ? "book" : "books"}
              {counts.published > 0 && <> · {counts.published} published</>}
              {counts.prelaunch > 0 && <> · {counts.prelaunch} pre-launch</>}
              {counts.draft > 0 && <> · {counts.draft} in progress</>}
            </p>
          </section>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
        <div className="flex flex-wrap gap-2 mb-8 border-b pb-3" style={{ borderColor: "var(--bf-border)" }} data-testid="author-filter-tabs">
          {filterTabs.map((t) => {
            const active = filter === t.key;
            const count = counts[t.key] ?? 0;
            return (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                className="text-sm px-3 py-2 rounded-md transition-colors"
                style={{
                  background: active ? "var(--bf-surface)" : "transparent",
                  color: active ? "var(--bf-primary)" : "var(--bf-text-muted)",
                  fontWeight: active ? 600 : 500,
                }}
                data-testid={`author-filter-${t.key}`}
              >
                {t.label}
                <span className="ml-2 text-xs opacity-70">{count}</span>
              </button>
            );
          })}
        </div>

        {visible.length === 0 ? (
          <p className="text-sm py-8" style={{ color: "var(--bf-text-muted)" }} data-testid="public-author-empty">
            No books in this phase yet.
          </p>
        ) : (
          <Accordion type="single" collapsible defaultValue={visible[0]?.book_id} className="space-y-4" data-testid="public-author-books-accordion">
            {visible.map((b) => {
              const s = STATUS_LABEL[b.status] || STATUS_LABEL.draft;
              const buy = b.buy_url || b.ads_url;
              return (
                <AccordionItem
                  key={b.book_id}
                  value={b.book_id}
                  className="rounded-lg overflow-hidden border-0"
                  style={{ background: "white", border: "1px solid var(--bf-border)" }}
                  data-testid={`public-author-book-${b.book_id}`}
                >
                  <AccordionTrigger
                    className="px-6 py-5 hover:no-underline gap-4 [&>svg]:ml-2"
                    data-testid={`public-author-book-trigger-${b.book_id}`}
                  >
                    <div className="flex items-center gap-4 flex-1 text-left min-w-0">
                      {b.cover_image_url ? (
                        <img
                          src={resolveCoverUrl(b.cover_image_url)}
                          alt="cover"
                          className="w-14 h-20 object-cover rounded flex-shrink-0"
                          style={{ border: "1px solid var(--bf-border)" }}
                        />
                      ) : (
                        <div
                          className="w-14 h-20 rounded flex items-center justify-center flex-shrink-0"
                          style={{ background: "var(--bf-surface)", border: "1px solid var(--bf-border)" }}
                        >
                          <BookOpen className="w-5 h-5" style={{ color: "var(--bf-text-muted)" }} />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                          <span
                            className="text-xs px-2 py-0.5 rounded uppercase tracking-wider"
                            style={{ background: s.bg, color: s.tone, fontWeight: 600, letterSpacing: "0.08em", fontSize: "0.65rem" }}
                            data-testid={`public-author-book-status-${b.book_id}`}
                          >
                            {s.label}
                          </span>
                          {b.published_date && (
                            <span className="text-xs flex items-center gap-1" style={{ color: "var(--bf-text-muted)" }}>
                              <Calendar className="w-3 h-3" /> {b.published_date}
                            </span>
                          )}
                          {b.chapter_count !== undefined && (
                            <span className="text-xs" style={{ color: "var(--bf-text-muted)" }}>
                              {b.chapter_count} {b.chapter_count === 1 ? "chapter" : "chapters"}
                            </span>
                          )}
                        </div>
                        <h3 className="font-serif text-2xl truncate">{b.title}</h3>
                        {b.theme && <p className="text-sm italic truncate" style={{ color: "var(--bf-text-muted)" }}>{b.theme}</p>}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-7" data-testid={`public-author-book-content-${b.book_id}`}>
                    <div className="grid md:grid-cols-3 gap-8 pt-2 border-t" style={{ borderColor: "var(--bf-border)" }}>
                      {/* Cover & meta column */}
                      <div className="pt-5">
                        {b.cover_image_url ? (
                          <img
                            src={resolveCoverUrl(b.cover_image_url)}
                            alt="Book cover"
                            className="w-full aspect-[2/3] object-cover rounded shadow-sm mb-5"
                            style={{ border: "1px solid var(--bf-border)" }}
                            data-testid={`public-author-book-cover-${b.book_id}`}
                          />
                        ) : (
                          <div
                            className="w-full aspect-[2/3] rounded flex items-center justify-center mb-5"
                            style={{ background: "var(--bf-surface)", border: "1px solid var(--bf-border)" }}
                            data-testid={`public-author-book-cover-${b.book_id}`}
                          >
                            <BookOpen className="w-10 h-10" style={{ color: "var(--bf-text-muted)" }} />
                          </div>
                        )}
                        <dl className="text-sm space-y-2" style={{ color: "var(--bf-text-muted)" }}>
                          {(b.genres || []).length > 0 && (
                            <div className="flex gap-2" data-testid={`public-author-book-genres-${b.book_id}`}>
                              <dt className="flex items-center gap-1 min-w-[5.5rem]"><Tag className="w-3 h-3" /> Genre</dt>
                              <dd className="text-[color:var(--bf-text)] flex-1">{b.genres.join(", ")}</dd>
                            </div>
                          )}
                          {b.isbn && (
                            <div className="flex gap-2" data-testid={`public-author-book-isbn-${b.book_id}`}>
                              <dt className="flex items-center gap-1 min-w-[5.5rem]"><BookCheck className="w-3 h-3" /> ISBN</dt>
                              <dd className="text-[color:var(--bf-text)] flex-1 break-all">{b.isbn}</dd>
                            </div>
                          )}
                          {b.published_date && (
                            <div className="flex gap-2">
                              <dt className="flex items-center gap-1 min-w-[5.5rem]"><Calendar className="w-3 h-3" /> Published</dt>
                              <dd className="text-[color:var(--bf-text)] flex-1">{b.published_date}</dd>
                            </div>
                          )}
                          {b.launch_date && b.status !== "published" && (
                            <div className="flex gap-2">
                              <dt className="flex items-center gap-1 min-w-[5.5rem]"><Calendar className="w-3 h-3" /> Launches</dt>
                              <dd className="text-[color:var(--bf-text)] flex-1">{b.launch_date}</dd>
                            </div>
                          )}
                        </dl>
                      </div>

                      {/* Summary & CTAs */}
                      <div className="md:col-span-2 pt-5 space-y-5">
                        {b.short_summary && (
                          <div data-testid={`public-author-book-summary-${b.book_id}`}>
                            <div className="label-eyebrow mb-2">Summary</div>
                            <p className="prose-book text-lg leading-relaxed">{b.short_summary}</p>
                          </div>
                        )}
                        {!b.short_summary && b.description && (
                          <div>
                            <div className="label-eyebrow mb-2">About</div>
                            <p className="prose-book text-lg leading-relaxed">{b.description}</p>
                          </div>
                        )}

                        <div className="flex flex-wrap gap-3 pt-2">
                          {buy && (
                            <a href={buy} target="_blank" rel="noreferrer" data-testid={`public-author-book-buy-${b.book_id}`}>
                              <Button className="text-white h-11 px-6" style={{ background: "var(--bf-primary)" }}>
                                <ShoppingCart className="w-4 h-4 mr-2" />
                                {b.status === "published" ? "Book your copy" : "Pre-order your copy"}
                              </Button>
                            </a>
                          )}
                          <Link to={`/${author.slug}/${b.slug}`} data-testid={`public-author-book-lifecycle-${b.book_id}`}>
                            <Button variant="outline" className="h-11 px-6">
                              See the book's lifecycle <ArrowUpRight className="w-4 h-4 ml-1" />
                            </Button>
                          </Link>
                        </div>

                        {(b.purchase_links || []).length > 0 && (
                          <div className="pt-3 border-t" style={{ borderColor: "var(--bf-border)" }}>
                            <div className="label-eyebrow mb-2">Also available at</div>
                            <div className="flex flex-wrap gap-2">
                              {b.purchase_links.map((link, idx) => (
                                <a
                                  key={link.url || idx}
                                  href={link.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sm px-3 py-1.5 rounded transition-colors hover:bg-[color:var(--bf-surface)]"
                                  style={{ border: "1px solid var(--bf-border)", color: "var(--bf-text)" }}
                                  data-testid={`public-author-book-retailer-${b.book_id}-${idx}`}
                                >
                                  {link.label} →
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </main>

      <footer className="py-10 border-t text-center text-sm" style={{ borderColor: "var(--bf-border)", color: "var(--bf-text-muted)" }}>
        Published with <Link to="/" className="underline">Bookforge</Link>
      </footer>
    </div>
  );
}
